import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import type {
  KocListItem,
  Platform,
  ProjectPageInput,
  ProjectPageSummary,
  ProjectPayload,
} from "@koc/shared";
import { apiKocs, apiProjects } from "../api.js";
import { KocPicker } from "../components/KocPicker.js";
import { Badge, Button, Card, Input, Label, PageHeader, Table, Textarea, fmtTime } from "../components/ui.js";

const PLATFORM_LABEL: Record<Platform, { short: string; tone: "blue" | "yellow" | "red" }> = {
  tiktok: { short: "TT", tone: "blue" },
  instagram: { short: "IG", tone: "yellow" },
  youtube: { short: "YT", tone: "red" },
};

function platformCounts(pages: ProjectPageSummary[]) {
  const counts: Record<Platform, number> = { tiktok: 0, instagram: 0, youtube: 0 };
  for (const a of pages) counts[a.platform]++;
  return counts;
}

export function ProjectsPage() {
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["projects", search], queryFn: () => apiProjects.list(search) });

  return (
    <>
      <PageHeader
        title="Projects"
        actions={<Button onClick={() => setShowAdd(true)}>+ Add project</Button>}
      />
      <div className="mb-3 max-w-sm">
        <Input
          placeholder="Search by project name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <Table
        rows={q.data?.items ?? []}
        onRowClick={(r) => navigate({ to: "/projects/$projectId", params: { projectId: String(r.id) } })}
        columns={[
          { header: "Project", cell: (r) => (
            <Link to="/projects/$projectId" params={{ projectId: String(r.id) }} className="text-stone-900 font-medium hover:underline">
              {r.account_name}
            </Link>
          )},
          { header: "Pages", cell: (r) => {
            const counts = platformCounts(r.pages);
            return (
              <div className="flex gap-1">
                {(Object.keys(counts) as Platform[]).map((p) => {
                  if (counts[p] === 0) return null;
                  const cfg = PLATFORM_LABEL[p];
                  return <Badge key={p} tone={cfg.tone}>{cfg.short}{counts[p] > 1 ? `×${counts[p]}` : ""}</Badge>;
                })}
              </div>
            );
          }},
          { header: "Keywords", cell: (r) => (
            <div className="flex flex-wrap gap-1 max-w-xs">
              {r.monitor_keywords.map((k) => <Badge key={k}>{k}</Badge>)}
            </div>
          )},
          { header: "Videos", cell: (r) => r.video_count, className: "text-right tabular-nums w-20" },
          { header: "Hits", cell: (r) => r.hit_count, className: "text-right tabular-nums w-20" },
          { header: "Enabled", cell: (r) => r.enabled ? <Badge tone="green">on</Badge> : <Badge tone="red">off</Badge> },
          { header: "Updated", cell: (r) => <span className="text-stone-500 text-xs">{fmtTime(r.updated_at)}</span> },
        ]}
      />
      {showAdd && <ProjectEditDialog onClose={() => setShowAdd(false)} />}
    </>
  );
}

type PageDraft = ProjectPageInput;

function emptyPage(): PageDraft {
  return { koc_id: null, platform: "tiktok", profile_url: "", label: "", enabled: true };
}

export function ProjectEditDialog({
  onClose,
  initial,
  initialId,
}: {
  onClose: () => void;
  initial?: ProjectPayload;
  initialId?: number;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Omit<ProjectPayload, "pages">>(() => {
    const seed = initial ?? {
      account_name: "",
      notes: "",
      monitor_keywords: [],
      enabled: true,
      pages: [],
    };
    const { pages: _ignore, ...rest } = seed;
    return rest;
  });
  const [pages, setPages] = useState<PageDraft[]>(initial?.pages ?? []);
  const [keywordsText, setKeywordsText] = useState((initial?.monitor_keywords ?? []).join(", "));

  const kocsQuery = useQuery({
    queryKey: ["kocs-all"],
    queryFn: () => apiKocs.list().then((r) => r.items),
  });
  const kocs: KocListItem[] = kocsQuery.data ?? [];

  const save = useMutation({
    mutationFn: () => {
      const payload: ProjectPayload = {
        ...form,
        monitor_keywords: keywordsText.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes || null,
        pages: pages.map((a) => ({
          ...(a.id !== undefined ? { id: a.id } : {}),
          koc_id: a.koc_id ?? null,
          platform: a.platform,
          profile_url: a.profile_url.trim(),
          label: a.label?.trim() || null,
          enabled: a.enabled,
        })),
      };
      return initialId ? apiProjects.update(initialId, payload).then(() => ({ id: initialId })) : apiProjects.create(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", initialId] });
      onClose();
    },
  });

  function updatePage(idx: number, patch: Partial<PageDraft>) {
    setPages((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }
  function removePage(idx: number) {
    setPages((prev) => prev.filter((_, i) => i !== idx));
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-[40rem] max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{initialId ? "Edit project" : "Add project"}</h2>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-3">
          <div>
            <Label>Project name</Label>
            <Input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} required />
          </div>

          <div>
            <Label>Pages</Label>
            <div className="space-y-2">
              {pages.length === 0 && (
                <div className="text-xs text-stone-500">No pages yet. Add at least one to enable scraping.</div>
              )}
              {pages.map((a, i) => (
                <div key={a.id ?? `new-${i}`} className="rounded border border-stone-200 bg-stone-50 p-2 space-y-2">
                  <div className="flex gap-2 items-center">
                    <select
                      value={a.platform}
                      onChange={(e) => updatePage(i, { platform: e.target.value as Platform })}
                      className="rounded border border-stone-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400"
                    >
                      <option value="tiktok">TikTok</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube">YouTube</option>
                    </select>
                    <Input
                      value={a.label ?? ""}
                      onChange={(e) => updatePage(i, { label: e.target.value })}
                      placeholder="label (e.g. Main, Brand)"
                      className="flex-1"
                    />
                    <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={a.enabled}
                        onChange={(e) => updatePage(i, { enabled: e.target.checked })}
                      />
                      on
                    </label>
                    <Button type="button" variant="ghost" onClick={() => removePage(i)} className="!px-2">×</Button>
                  </div>
                  <Input
                    value={a.profile_url}
                    onChange={(e) => updatePage(i, { profile_url: e.target.value })}
                    placeholder="https://…"
                    required
                  />
                  <div className="flex gap-2 items-center">
                    <Label className="!mb-0 whitespace-nowrap text-xs">KOC</Label>
                    <KocPicker
                      kocs={kocs}
                      value={a.koc_id ?? null}
                      onChange={(kocId) => updatePage(i, { koc_id: kocId })}
                      placeholder="search KOC… (add new on KOCs page)"
                    />
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="secondary" className="mt-2" onClick={() => setPages([...pages, emptyPage()])}>
              + Add page
            </Button>
            <p className="text-xs text-stone-500 mt-1">YouTube: paste channel or shorts URL — only Shorts videos get tracked.</p>
          </div>

          <div>
            <Label>Monitor keywords (comma-separated, lowercased on save)</Label>
            <Input value={keywordsText} onChange={(e) => setKeywordsText(e.target.value)} placeholder="brand, sponsor, ad" />
          </div>
          <div>
            <Label>Project notes</Label>
            <Textarea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            Enabled (daily cron will scrape enabled pages)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
